<template>
  <div>
    <!-- Desktop Categories -->
    <v-col cols="12" v-if="display.mdAndUp.value">
      <v-card class="pa-2 no-shadow-card" elevation="0" :loading="loading">
        <v-row class="text-left">
          <v-col
            cols="12"
            md="3"
            v-for="(category, index) in featuredCategories"
            :key="index"
          >
            <v-card
              class="mx-auto mt-3 my-4 no-shadow-card"
              elevation="0"
              outlined
            >
              <v-card-title class="cat-head pa-0 pb-2">
                <v-img
                  :src="require(`@/assets/${category.icon}.svg`)"
                  alt="icon"
                  class="cat-icon mr-2"
                  width="20"
                  height="20"
                  style="
                    filter: brightness(0) saturate(100%) invert(60%) sepia(100%)
                      saturate(1000%) hue-rotate(15deg) brightness(90%);
                  "
                  cover
                />
                <span
                  class="cat-title font-secondary text-base font-bold text-primary-color"
                >
                  {{ category.title }}
                </span>
              </v-card-title>

              <v-card-text class="cat-body">
                <v-list density="compact" class="pa-0">
                  <v-list-item
                    v-for="(sub, i) in category.subcategories"
                    :key="i"
                    class="px-0 py-1 hover-effect cat-title-below"
                    @click="handleSubcategoryClick(category, sub)"
                  >
                    <v-list-item-content class="py-0">
                      <v-list-item-title
                        class="font-primary text-sm font-regular text-primary-color"
                      >
                        {{ sub.title }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    class="px-0 py-1"
                    @click="handleCategoryClick(category)"
                  >
                    <v-list-item-content class="py-0">
                      <v-list-item-title
                        class="font-primary text-sm font-bold"
                        style="color: darkgoldenrod !important"
                      >
                        {{ category.allInText }}
                        <span><v-icon icon="mdi-arrow-right"></v-icon></span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- Mobile Categories -->
    <div class="mx-auto bg-white ma-1" v-if="display.mobile.value">
      <v-row>
        <v-col
          :cols="index === mobileCategories.length - 1 ? '4' : '4'"
          v-for="(category, index) in mobileCategories"
          :key="index"
          class=""
        >
          <v-card
            height="102"
            :width="index === mobileCategories.length - 1 ? '' : ''"
            @click="handleMobileCategoryClick(category)"
            elevation="1"
            class="my-auto ma-n2 no-shadow-card"
          >
            <v-row class="text-center">
              <v-col cols="12" class="d-flex mt-10 justify-center">
                <v-img
                  :src="require(`@/assets/${category.slug}.svg`)"
                  alt="icon"
                  max-width="25"
                  max-height="25"
                  style="
                    filter: brightness(0) saturate(100%) invert(60%) sepia(100%)
                      saturate(1000%) hue-rotate(15deg) brightness(90%);
                  "
                  class=""
                ></v-img>
              </v-col>
              <v-col cols="12" class="text-center mt-n6 mb-4">
                <div
                  class="font-primary text-xs font-bold text-primary-color text-center"
                >
                  {{ category.title }}
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup>
import { useFont } from "@/composables/useFont";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useStore } from "vuex";

// Props
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  featuredCategories: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits([
  "categorySelected",
  "subcategorySelected",
  "mobileCategorySelected",
]);

// Composables
const display = useDisplay();
const store = useStore();
const router = useRouter();

const { fontClasses } = useFont();

let isNavigating = false;

// ✅ ENHANCED: Static data with category and subcategory IDs
const STATIC_FEATURED_CATEGORIES = [
  {
    id: 1,
    icon: "motors",
    title: "Motors",
    slug: "motors",
    subcategories: [
      { id: 1, title: "Cars", slug: "cars" },
      { id: 2, title: "Motorcycles", slug: "motorcycles" },
      { id: 3, title: "Auto Accessories & Parts", slug: "auto-accessories" },
      { id: 4, title: "Heavy Vehicles", slug: "heavy-vehicles" },
    ],
    allInText: "All in Motors",
  },
  {
    id: 3,
    icon: "property-for-sale",
    title: "Property for Sale",
    slug: "property-for-sale",
    subcategories: [
      { id: 7, title: "Residential", slug: "residential-sale" },
      { id: 8, title: "Commercial", slug: "commercial-sale" },
      { id: 9, title: "Land", slug: "land-sale" },
      { id: 10, title: "Multiple Units", slug: "multiple-units" },
    ],
    allInText: "All in Property for Sale",
  },
  {
    id: 4,
    icon: "property-for-rent",
    title: "Property for Rent",
    slug: "property-for-rent",
    subcategories: [
      { id: 11, title: "Residential", slug: "residential-rent" },
      { id: 12, title: "Commercial", slug: "commercial-rent" },
      { id: 13, title: "Rooms For Rent", slug: "rooms-for-rent" },
      { id: 14, title: "Monthly Short Term", slug: "monthly-short-term" },
    ],
    allInText: "All in Property for Rent",
  },
  {
    id: 2,
    icon: "jobs",
    title: "Jobs",
    slug: "jobs",
    subcategories: [{ id: 16, title: "I'm Hiring", slug: "hiring" }],
    allInText: "All in Jobs",
  },
  {
    id: 5,
    icon: "community",
    title: "Community",
    slug: "community",
    subcategories: [
      { id: 22, title: "Freelancers", slug: "freelancers" },
      { id: 24, title: "Home Maintenance", slug: "home-maintenance" },
      { id: 26, title: "Other Services", slug: "other-services" },
      { id: 28, title: "Tutors & Classes", slug: "tutors-classes" },
    ],
    allInText: "All in Community",
  },
  {
    id: 6,
    icon: "classified",
    title: "Classifieds",
    slug: "classified",
    subcategories: [
      { id: 30, title: "Electronics", slug: "electronics" },
      { id: 31, title: "Computers & Networking", slug: "computers-networking" },
      { id: 35, title: "Clothing & Accessories", slug: "clothing-accessories" },
      { id: 37, title: "Jewelry & Watches", slug: "jewelry-watches" },
    ],
    allInText: "All in Classifieds",
  },
  {
    id: 7,
    icon: "furniture-home-garden",
    title: "Furniture & Garden",
    slug: "furniture-garden",
    subcategories: [
      {
        id: 50,
        title: "Furniture, Home & Garden",
        slug: "furniture-home-garden",
      },
      { id: 33, title: "Home Appliances", slug: "home-appliances" },
    ],
    allInText: "All in Furniture & Garden",
  },
  {
    id: 8,
    icon: "mobile-phones-pdas",
    title: "Mobile Phones & Tablets",
    slug: "mobile-phones-tablets",
    subcategories: [
      {
        id: 51,
        title: "Mobile Phones & Tablets",
        slug: "mobile-phones-tablets",
      },
    ],
    allInText: "All in Mobile Phones & Tablets",
  },
];

// Use static data for featured categories
const featuredCategories = computed(() => STATIC_FEATURED_CATEGORIES);

// Computed properties
const mobileCategories = computed(() => props.categories);

// ✅ ENHANCED: Property category IDs for routing decisions
const PROPERTY_CATEGORY_IDS = [3, 4]; // Property for Sale (3) and Property for Rent (4)

// ✅ ENHANCED: Handle subcategory clicks with proper IDs
const handleSubcategoryClick = async (category, subcategory) => {
  console.log("🎯 Subcategory clicked:", {
    category: category.title,
    subcategory: subcategory.title,
    categoryId: category.id,
    subcategoryId: subcategory.id,
  });

  // ✅ CRITICAL: Ensure we have valid IDs
  if (!category.id) {
    console.error(
      "❌ Category missing ID, falling back to category-only navigation"
    );
    return handleCategoryClick(category);
  }

  if (!subcategory.id) {
    console.error(
      "❌ Subcategory missing ID, falling back to category-only navigation"
    );
    return handleCategoryClick(category);
  }

  if (isNavigating) return;
  isNavigating = true;

  try {
    // Update store with both category and subcategory
    await store.dispatch("filter/setSelectedAttributes", {
      category: category,
      subcategory: subcategory,
    });
    // Emit events for parent components
    //emit('categorySelected', category);
    //emit('subcategorySelected', { category, subcategory });
    // Navigate with filters
    navigateWithFilters(category, subcategory);
  } catch (error) {
    console.error("❌ Error handling subcategory click:", error);
    // Fallback to category-only navigation
    handleCategoryClick(category);
  }
};

// ✅ ENHANCED: Handle category clicks with proper validation
const handleCategoryClick = async (category) => {
  console.log("🎯 Category clicked:", {
    category: category.title,
    categoryId: category.id,
  });

  // ✅ CRITICAL: Ensure we have valid category ID
  if (!category.id) {
    console.error("❌ Category missing ID, cannot navigate");
    return;
  }

  try {
    // Update store with category only
    await store.dispatch("filter/setSelectedAttributes", category);
    //emit('categorySelected', category);

    // Navigate with category filter only
    navigateWithFilters(category, null);
  } catch (error) {
    console.error("❌ Error handling category click:", error);
    // Ultimate fallback
    router.push({ name: "searchResults" });
  }
};

// ✅ NEW: Smart navigation with filter parameters
const navigateWithFilters = (category, subcategory = null) => {
  const isPropertyCategory = PROPERTY_CATEGORY_IDS.includes(category.id);

  console.log("🧭 Navigation decision:", {
    categoryId: category.id,
    categorySlug: category.slug,
    isPropertyCategory,
    hasSubcategory: !!subcategory,
    subcategoryId: subcategory?.id,
    targetComponent: isPropertyCategory
      ? "PropertySearchResultParentComponent"
      : "GeneralFiltersResultsPage",
  });

  if (isPropertyCategory) {
    // Route to Property Search Results Component
    navigateToPropertyResults(category, subcategory);
  } else {
    // Route to General Filters Results Page
    navigateToGeneralResults(category, subcategory);
  }
};

// ✅ ENHANCED: Property navigation with proper parameters
const navigateToPropertyResults = (category, subcategory = null) => {
  const routeParams = {
    categorySlug: category.slug,
  };

  const queryParams = {
    categoryId: category.id,
  };

  // Add subcategory if available
  if (subcategory && subcategory.id) {
    routeParams.subcategorySlug =
      subcategory.slug || generateSlug(subcategory.title);
    queryParams.subcategoryId = subcategory.id;
  }

  console.log("🏠 Navigating to Property Results:", {
    route: "PropertySearch",
    params: routeParams,
    query: queryParams,
  });

  router
    .push({
      name: "PropertySearch",
      params: routeParams,
      query: queryParams,
    })
    .catch((error) => {
      console.warn("⚠️ Property navigation failed, trying fallback:", error);
      // Fallback: Navigate to general search with query parameters
      router.push({
        name: "searchResults",
        query: queryParams,
      });
    });
};

// ✅ ENHANCED: Smart navigation with proper query parameters
const navigateToGeneralResults = (category, subcategory = null) => {
  const queryParams = {
    categoryId: category.id, // ✅ Use categoryId instead of category
  };

  // Add subcategory if available
  if (subcategory && subcategory.id) {
    queryParams.subcategoryId = subcategory.id; // ✅ Use subcategoryId instead of subcategory
  }

  console.log("📋 Navigating to General Results:", {
    route: "searchResults",
    query: queryParams,
  });

  router
    .push({
      name: "searchResults",
      query: queryParams,
    })
    .catch((error) => {
      console.warn("⚠️ General navigation failed, trying simple route:", error);
      // Ultimate fallback
      router.push({ name: "searchResults" });
    });
};

// ✅ ENHANCED: Handle mobile category clicks
const handleMobileCategoryClick = (category) => {
  console.log("📱 Mobile category clicked:", category.title);
  handleCategoryClick(category);
};

// ✅ LEGACY SUPPORT: Keep existing methods for backward compatibility
const findCategory = (selectedCategory) => {
  const category = props.categories.find(
    (cat) => cat.slug === selectedCategory.slug
  );

  if (category) {
    store.dispatch("filter/setSelectedAttributes", category);

    // Use new navigation logic
    navigateWithFilters(category, null);
  } else {
    console.log("No category found");
  }
};

const handleCategorySelection = (category) => {
  if (!category) {
    router.push({ name: "searchResults" });
  } else {
    navigateWithFilters(category, null);
  }
};

// ✅ UTILITY: Generate URL-friendly slug
const generateSlug = (text) => {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};
</script>

<style scoped>
/* Text class for compact and bold styling */
.text-compact {
  letter-spacing: -0.5px; /* Reduce space between letters */
  line-height: 1.2; /* Reduce space between lines */
}

.hover-effect {
  background-color: transparent !important;
  transition: color 0.3s ease;
  cursor: pointer;
}

.hover-effect:hover {
  background-color: transparent !important;
  color: blue !important;
}

.v-list-item-title {
  color: inherit;
}

/* Add visual feedback for clickable items */
.v-list-item:hover {
  background-color: transparent !important; /* No background color change */
  color: blue !important; /* Transition to blue on hover */
}

.no-shadow-card {
  box-shadow: none !important; /* Remove all shadows */
  transition: none !important; /* Disable hover shadow effects */
  margin-bottom: 0 !important;
  padding: 0.1px !important;
  border: none !important; /* Remove borders completely */
  box-shadow: #ffffff 0px 0px 0px !important; /* Ensure no shadow */
}

.no-shadow-card:hover {
  box-shadow: none !important; /* Ensure no shadow on hover */
}

.v-card {
  background-color: #ffffff; /* Ensure consistent background color */
  border-radius: 0.1px; /* Optional: Reduce border radius for compact styling */
}

.v-row {
  gap: 0 !important; /* Remove gaps between rows */
}

.v-col {
  margin-bottom: 0 !important; /* Remove bottom margin between columns */
}

/* Desktop category header: icon + title on one line, perfectly aligned */
.cat-head {
  display: flex;
  align-items: center; /* vertical centering of icon with text */
  gap: 3px;
}

.image-list-cat {
  position: relative;
  z-index: 20;
  margin-right: 50px !important;
}

.cat-icon {
  flex: 0 0 20px; /* fixed icon box so titles line up across columns */
  display: block; /* avoid inline-img descender gap */
  color: darkgoldenrod !important;
}

.cat-title {
  line-height: 1.1; /* snug with icon */
  white-space: nowrap; /* keep title on one line (as in your reference) */
}

/* Align subcategory list exactly under the title text (not the icon) */
.cat-head {
  gap: 4px;
  margin-left: 20px;
} /* you already have this */
.cat-icon {
  width: 20px;
  height: 20px;
} /* fixed size for math below */

/* Indent the list by icon width + gap so it lines up with the title text */
.cat-body {
  padding-left: calc(20px + 1px);
}

/* Remove Vuetify list default start padding so all items share the same start line */
.cat-body .v-list {
  padding: 0 !important;
}
.cat-body .v-list-item {
  --v-list-item-padding-start: 0px; /* nuke built-in left padding */
  --v-list-item-padding-end: 0px;
  padding-inline-start: 0 !important;
  padding-inline-end: 0 !important;
  min-height: 28px; /* tidy rows; adjust if you want tighter */
}
.cat-body .v-list-item .v-list-item__content {
  padding: 0 !important;
}
.cat-body .v-list-item-title {
  margin: 0 !important;
}

.cat-title-below {
  margin-right: 50px !important;
}
</style>
